import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy } from '@angular/core';

const HANDLE_CLASS = 'th-col-resize-handle';
const MIN_WIDTH = 30;
const DRAG_THRESHOLD = 3;

/**
 * Permite cambiar el ancho de las columnas de cualquier <table> arrastrando el borde derecho de su <th>.
 * - Zona de agarre de ~18px en cada borde (10px a cada lado, dentro de cada th), visible al pasar el ratón.
 * - Engancha cabeceras creadas después (ngFor, mat-table, cambios de columnas) vía MutationObserver sobre el thead.
 * - Al primer arrastre congela los anchos de la fila de cabecera con table-layout: fixed; así las filas
 *   que se rendericen después (virtual-scroll) heredan el ancho sin tocar ningún <td>.
 * - Si ningún contenedor puede hacer scroll horizontal, el ancho se toma de la columna vecina (modo vecino).
 * - Doble clic en el agarre: restablece los anchos originales de la tabla.
 * - Añadir el atributo `noColResize` a una tabla para excluirla.
 */
@Directive({
  selector: 'table'
})
export class TableColResizeDirective implements AfterViewInit, OnDestroy {
  private table!: HTMLTableElement;
  private tableObserver: MutationObserver | null = null;
  private theadObserver: MutationObserver | null = null;
  private observedThead: HTMLElement | null = null;
  private rafId = 0;
  private frozen = false;

  // estado del arrastre
  private targetTh: HTMLElement | null = null;
  private neighborTh: HTMLElement | null = null;
  private activeHandle: HTMLElement | null = null;
  private startX = 0;
  private startWidth = 0;
  private startNeighborWidth = 0;
  private startTableWidth = 0;
  private moved = false;

  constructor(private el: ElementRef, private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.table = this.el.nativeElement as HTMLTableElement;
    if (this.table.hasAttribute('noColResize')) return;

    this.zone.runOutsideAngular(() => {
      this.attachHandles();
      this.observeThead();
      // solo hijos directos: detecta la creación/sustitución del thead sin reaccionar al reciclado de filas del tbody
      this.tableObserver = new MutationObserver(() => {
        this.observeThead();
        this.scheduleAttach();
      });
      this.tableObserver.observe(this.table, { childList: true });
    });
  }

  private observeThead(): void {
    const thead = this.table.tHead as HTMLElement | null;
    if (thead === this.observedThead) return;
    this.theadObserver?.disconnect();
    this.observedThead = thead;
    if (!thead) return;
    this.theadObserver = new MutationObserver(() => this.scheduleAttach());
    this.theadObserver.observe(thead, { childList: true, subtree: true });
  }

  private scheduleAttach(): void {
    if (this.rafId) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = 0;
      this.attachHandles();
    });
  }

  private attachHandles(): void {
    try {
      const ths = Array.from(this.table.querySelectorAll('th')) as HTMLElement[];
      ths.forEach(th => {
        // no enganchar cabeceras de tablas anidadas
        if (th.closest('table') !== this.table) return;

        const position = window.getComputedStyle(th).position;
        if (!position || position === 'static') th.style.position = 'relative';

        // Los agarres no sobresalen del th: las cabeceras sticky crean su propio contexto de apilamiento y el th
        // vecino taparía la parte que sobresale. Por eso cada borde tiene dos agarres: uno dentro del borde
        // derecho del th y otro dentro del borde izquierdo del th siguiente; los dos redimensionan la misma columna.
        this.ensureHandle(th, false);
        const prev = th.previousElementSibling;
        if (prev && prev.tagName === 'TH') {
          this.ensureHandle(th, true);
        } else {
          th.querySelector(`:scope > .${HANDLE_CLASS}.is-left`)?.remove();
        }
      });
    } catch (e) {
      // noop
    }
  }

  private ensureHandle(th: HTMLElement, left: boolean): void {
    const selector = `:scope > .${HANDLE_CLASS}${left ? '.is-left' : ':not(.is-left)'}`;
    if (th.querySelector(selector)) return;

    const handle = document.createElement('span');
    handle.className = left ? `${HANDLE_CLASS} is-left` : HANDLE_CLASS;
    handle.addEventListener('pointerdown', (ev: PointerEvent) => {
      // el agarre izquierdo redimensiona la columna anterior
      const target = left ? th.previousElementSibling as HTMLElement | null : th;
      if (target && target.tagName === 'TH') this.onPointerDown(ev, target, handle);
    });
    handle.addEventListener('click', (ev: MouseEvent) => { ev.stopPropagation(); ev.preventDefault(); });
    handle.addEventListener('dblclick', (ev: MouseEvent) => { ev.stopPropagation(); ev.preventDefault(); this.resetWidths(); });
    th.appendChild(handle);
  }

  private headerCells(th: HTMLElement): HTMLElement[] {
    return (Array.from(th.parentElement?.children ?? []) as HTMLElement[]).filter(c => c.tagName === 'TH');
  }

  private setImportant(el: HTMLElement, prop: string, value: string): void {
    el.style.setProperty(prop, value, 'important');
  }

  private setColWidth(th: HTMLElement, width: number): void {
    const px = `${width}px`;
    // los anchos se miden con getBoundingClientRect (incluye padding y borde)
    this.setImportant(th, 'box-sizing', 'border-box');
    this.setImportant(th, 'width', px);
    this.setImportant(th, 'min-width', px);
    this.setImportant(th, 'max-width', px);
  }

  /** Congela los anchos actuales de la fila de cabecera para que table-layout: fixed los respete. */
  private freeze(th: HTMLElement): void {
    if (this.frozen) return;
    const cells = this.headerCells(th);
    const widths = cells.map(c => c.getBoundingClientRect().width);
    cells.forEach((c, i) => this.setColWidth(c, widths[i]));
    this.setImportant(this.table, 'table-layout', 'fixed');
    this.setImportant(this.table, 'width', `${this.table.getBoundingClientRect().width}px`);
    this.setImportant(this.table, 'min-width', '0');
    this.setImportant(this.table, 'max-width', 'none');
    this.frozen = true;
  }

  /** true si algún ancestro permite scroll horizontal, es decir, la tabla puede crecer sin quedar recortada. */
  private canGrow(): boolean {
    let node = this.table.parentElement;
    while (node && node !== document.body) {
      const ox = window.getComputedStyle(node).overflowX;
      if (ox === 'auto' || ox === 'scroll') return true;
      if (ox === 'hidden' || ox === 'clip') return false;
      node = node.parentElement;
    }
    return false;
  }

  private onPointerDown(event: PointerEvent, th: HTMLElement, handle: HTMLElement): void {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();

    this.freeze(th);
    this.targetTh = th;
    this.activeHandle = handle;
    this.startX = event.clientX;
    this.startWidth = th.getBoundingClientRect().width;
    this.startTableWidth = this.table.getBoundingClientRect().width;
    this.moved = false;

    const next = th.nextElementSibling as HTMLElement | null;
    this.neighborTh = !this.canGrow() && next && next.tagName === 'TH' ? next : null;
    this.startNeighborWidth = this.neighborTh ? this.neighborTh.getBoundingClientRect().width : 0;

    try { handle.setPointerCapture(event.pointerId); } catch (e) {}
    handle.classList.add('active');
    document.body.classList.add('col-resizing');
    document.addEventListener('pointermove', this.onPointerMove);
    document.addEventListener('pointerup', this.onPointerUp);
    document.addEventListener('pointercancel', this.onPointerUp);
  }

  private onPointerMove = (event: PointerEvent): void => {
    if (!this.targetTh) return;
    let delta = event.clientX - this.startX;
    if (Math.abs(delta) > DRAG_THRESHOLD) this.moved = true;

    if (this.neighborTh) {
      // modo vecino: el ancho total no cambia
      const max = this.startNeighborWidth - MIN_WIDTH;
      const min = MIN_WIDTH - this.startWidth;
      delta = Math.max(min, Math.min(max, delta));
      this.setColWidth(this.targetTh, this.startWidth + delta);
      this.setColWidth(this.neighborTh, this.startNeighborWidth - delta);
    } else {
      const newWidth = Math.max(MIN_WIDTH, this.startWidth + delta);
      this.setColWidth(this.targetTh, newWidth);
      this.setImportant(this.table, 'width', `${this.startTableWidth + (newWidth - this.startWidth)}px`);
    }
  };

  private onPointerUp = (): void => {
    if (this.moved) {
      // evitar que el click posterior al arrastre ordene la columna (mat-sort-header)
      const swallow = (ev: MouseEvent) => { ev.stopPropagation(); ev.preventDefault(); };
      document.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => document.removeEventListener('click', swallow, { capture: true }), 0);
    }
    this.endDrag();
  };

  private endDrag(): void {
    this.activeHandle?.classList.remove('active');
    document.body.classList.remove('col-resizing');
    document.removeEventListener('pointermove', this.onPointerMove);
    document.removeEventListener('pointerup', this.onPointerUp);
    document.removeEventListener('pointercancel', this.onPointerUp);
    this.targetTh = null;
    this.neighborTh = null;
    this.activeHandle = null;
  }

  /** Quita los anchos aplicados y devuelve la tabla a su diseño original. */
  private resetWidths(): void {
    const props = ['width', 'min-width', 'max-width'];
    (Array.from(this.table.querySelectorAll('th')) as HTMLElement[]).forEach(th => [...props, 'box-sizing'].forEach(p => th.style.removeProperty(p)));
    [...props, 'table-layout'].forEach(p => this.table.style.removeProperty(p));
    this.frozen = false;
  }

  ngOnDestroy(): void {
    this.endDrag();
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.tableObserver?.disconnect();
    this.theadObserver?.disconnect();
    try {
      this.table?.querySelectorAll(`.${HANDLE_CLASS}`).forEach(h => h.remove());
    } catch (e) {}
  }
}
